import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * Photo-only gallery. Videos live in the backend but are intentionally
 * excluded here so /photos remains a focused photograph experience.
 */
const FILTERS = [
  "All",
  "Featured",
  "Fades",
  "Beards",
  "Women",
  "Designs",
  "Color",
  "Texture",
  "Kids",
];

export default function Gallery({ media }) {
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);

  // Guarantee photos only — even if a video slips into props.
  const photos = useMemo(
    () => (media || []).filter((m) => m.type !== "video"),
    [media]
  );

  const filtered = useMemo(() => {
    if (filter === "All") return photos;
    if (filter === "Featured") return photos.filter((m) => m.featured);
    return photos.filter((m) => {
      const cats = m.categories || [m.category];
      return cats.includes(filter);
    });
  }, [filter, photos]);

  return (
    <section
      id="gallery"
      className="relative bg-black text-white py-24 md:py-40 border-t border-white/10 overflow-hidden"
      data-testid="gallery-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14 md:mb-20 items-end">
          <div className="col-span-12 md:col-span-6">
            <div className="eyebrow mb-6">§ The Portfolio</div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-serif text-4xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight"
            >
              Photographs.
              <br />
              <span className="italic text-zinc-400">
                Real work. Real clients.
              </span>
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:pl-8">
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              A living archive of cuts, fades, beards, color, twists, and designs — every image is authentic She Cuts work.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 md:gap-3 mb-10">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`gallery-filter-${f.toLowerCase()}`}
              className={`px-4 md:px-5 py-2 md:py-2.5 rounded-full border eyebrow text-[10px] md:text-[11px] transition-all ${
                filter === f
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-white border-white/25 hover:border-white/60"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <AnimatePresence>
            {filtered.map((m, i) => (
              <motion.button
                layout
                key={m.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.55, delay: (i % 8) * 0.03 }}
                onClick={() => setOpen(m)}
                data-testid={`gallery-item-${m.id}`}
                className={`group relative overflow-hidden bg-zinc-900 border border-white/10 ${
                  i % 5 === 0 || i % 7 === 0 ? "row-span-2 aspect-[3/5]" : "aspect-[3/4]"
                }`}
              >
                <img
                  src={m.url}
                  alt={m.caption || "She Cuts portfolio"}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-left translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="eyebrow text-white/70 text-[9px]">
                    {(m.categories || [m.category]).join(" · ")}
                  </div>
                  <div className="font-serif text-lg mt-1">{m.caption}</div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>

        {filtered.length === 0 && (
          <div className="text-center text-zinc-500 py-16 eyebrow">
            No photos in this category yet.
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setOpen(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setOpen(null)}
              className="absolute top-6 right-6 w-11 h-11 rounded-full border border-white/25 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X className="w-4 h-4" strokeWidth={1.4} />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.7, 0, 0.2, 1] }}
              className="relative max-w-5xl max-h-[85vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={open.url}
                alt={open.caption}
                className="w-full max-h-[85vh] object-contain"
              />
              <div className="mt-4 text-center">
                <div className="eyebrow text-zinc-500">
                  {(open.categories || [open.category]).join(" · ")}
                </div>
                <div className="font-serif text-xl mt-1">{open.caption}</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
